// Divisão de pedido grande em N filhos (1234/A, 1234/B, ...).
//
// Modelo:
//   • Pedido mãe ganha:
//       - dividido: true
//       - filhosIds: [id1, id2, ...]
//   • Cada pedido filho tem:
//       - tipoFilho: true                (flag pra filtrar de métricas)
//       - pedidoMaeId / pedidoMaeNumero (link pro mãe)
//       - letraParte: 'A' | 'B' | ...    (visual)
//       - totalPartes: N                 (visual)
//       - numero: `${mae.numero}/A`      (visual)
//       - moveis: subset dos móveis do mãe
//       - valorTotal: 0                  (valor fica no mãe)
//       - prazo: data independente da parte
//       - status: 'CADASTRADO' (segue Kanban padrão)
//
// Pedido mãe dividida:
//   • Não aparece mais na Logística nem no Kanban (filhos assumem)
//   • Continua na lista de Pedidos e em "A receber" — é a referência
//     financeira/comercial.
//   • Quando todos filhos ficam ENTREGUE, gestor marca mãe como ENTREGUE
//     (manual por enquanto; automático no futuro se pedirem).

const LETRAS_PARTE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

function novoIdFilho() {
  const ts = Date.now().toString(36)
  const rnd = Math.random().toString(36).slice(2, 6)
  return `pf_${ts}_${rnd}`
}

function numeroFilho(mae, letra) {
  const base = mae.numero || mae.numeroErp || mae.id || 'sem-num'
  return `${base}/${letra}`
}

// partes: array de objetos { moveis: [...subset], prazo: 'YYYY-MM-DD', observacao: '' }
// Retorna: { mae: pedidoMaeAtualizado, filhos: [pedido1, pedido2, ...] }
export function dividirPedido(mae, partes) {
  if (!Array.isArray(partes) || partes.length < 2) {
    throw new Error('Divisão precisa de pelo menos 2 partes')
  }
  if (partes.length > LETRAS_PARTE.length) {
    throw new Error(`Divisão máxima: ${LETRAS_PARTE.length} partes`)
  }
  const agoraIso = new Date().toISOString()
  const totalPartes = partes.length

  const filhos = partes.map((parte, i) => {
    const letra = LETRAS_PARTE[i]
    const id    = novoIdFilho()
    return {
      id,
      numero:               numeroFilho(mae, letra),

      // ── Marcadores de filho ──────────────────────────────────────────
      tipoFilho:            true,
      pedidoMaeId:          mae.id,
      pedidoMaeNumero:      mae.numero,
      letraParte:           letra,
      totalPartes,

      // ── Status / Kanban padrão ──────────────────────────────────────
      status:               'CADASTRADO',
      prazo:                parte.prazo || mae.prazo || '',
      criadoEm:             agoraIso,

      // ── Subset de móveis ────────────────────────────────────────────
      moveis:               Array.isArray(parte.moveis) ? parte.moveis.map(m => ({ ...m })) : [],

      // ── Cliente herdado ─────────────────────────────────────────────
      cliente:              { ...(mae.cliente || {}) },
      vendedora:            mae.vendedora || '',

      // ── Financeiro: zero (valor fica no mãe) ────────────────────────
      valorTotal:           0,
      valorPendenteEntrega: 0,
      formaPagamento:       '',

      // ── Logística ───────────────────────────────────────────────────
      horarioEntrega:       '',
      confirmacaoEntrega:   '',
      enderecoEntrega:      '',
      observacoesLogistica: parte.observacao || '',
      atencao:              false,
      prazoOriginal:        '',
      pagoEntrega:          false,
      // Itens e outros campos pesados ficam no mãe — filho é leve
      itens:                [],
      resumo:               `Parte ${letra} de ${totalPartes} — Pedido #${mae.numero || ''}${parte.observacao ? ' — ' + parte.observacao : ''}`,
    }
  })

  const maeAtualizada = {
    ...mae,
    dividido:    true,
    filhosIds:   filhos.map(f => f.id),
    divididoEm:  agoraIso,
  }

  return { mae: maeAtualizada, filhos }
}

// Recolhe a divisão: apaga os filhos e remove flag do mãe.
// Útil caso o gestor erre e queira refazer.
export function cancelarDivisao(mae) {
  return {
    ...mae,
    dividido:   false,
    filhosIds:  [],
    divididoEm: null,
  }
}

// Helper: dado o pedido mãe e a lista completa de pedidos, retorna os filhos
export function obterFilhos(mae, todosPedidos) {
  if (!mae?.dividido || !Array.isArray(mae.filhosIds)) return []
  const ids = new Set(mae.filhosIds)
  return todosPedidos.filter(p => ids.has(p.id))
}

// Helper: status agregado dos filhos
//   Se todos ENTREGUE → ENTREGUE
//   Senão → "Em andamento (X/Y)"
export function statusAgregadoFilhos(filhos) {
  if (!filhos?.length) return null
  const entregues = filhos.filter(f => f.status === 'ENTREGUE').length
  return {
    entregues,
    total: filhos.length,
    todosEntregues: entregues === filhos.length,
  }
}
